'use client'

import Logo from "@/components/Logo";
import { Proposal } from "@/models/proposal.model";
import { FormValues } from "@/models/proposalForm.model";
import { cepFormatter } from "@/utils/cepFormatter";
import { cpfFormatter } from "@/utils/cpfFormatter";
import {
  Box,
  Checkbox,
  Text,
  VStack,
  Button,
  Flex,
  Icon,
  Heading,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  // keyframes,
} from "@chakra-ui/react";
import { useState } from "react";
import { MdDescription } from "react-icons/md";

interface ConfirmationStepProps {
  formData: FormValues;
  proposal?: Proposal;
  handleSubmit: () => void;
}

export default function Component({ formData, handleSubmit, proposal }: ConfirmationStepProps) {
  const [isChecked, setIsChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  function getPixTypeLabel(value?: string): string {
    switch (value) {
      case "EMAIL":
        return "Email";
      case "PHONE":
        return "Celular";
      case "DOCUMENT":
        return "CPF";
      default:
        return "Desconhecido";
    }
  }

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  const handleConfirmSubmit = () => {
    setIsLoading(true);
    handleSubmit();
  };

  // const pulse = keyframes`
  //   0% { transform: scale(1); }
  //   50% { transform: scale(1.1); }
  //   100% { transform: scale(1); }
  // `;

  return (
    <Flex justify="center" p={{ base: 4, md: 8 }}>
      <Box
        p={{ base: 6, md: 6 }}
        borderWidth="1px"
        borderRadius="md"
        boxShadow="base"
        bg="white"
        w={{ base: "95%", md: "60%", lg: "40%" }}
      >
        <Flex align="center" mb={6} justify="center">
          <Icon
            as={MdDescription}
            boxSize={{ base: 8, md: 6 }}
            color="gray.500"
            mr={3}
          />
          <Heading
            size={{ base: "lg", md: "lg" }}
            color="gray.700"
            fontFamily="Lexend Deca"
          >
            Revisar Proposta
          </Heading>
        </Flex>

        <VStack spacing={{ base: 6, md: 4 }} align="stretch">
          <Box>
            <Text
              fontSize={{ base: "lg", md: "md" }}
              color="gray.600"
              mb={2}
              fontFamily="Lexend Deca"
            >
              Nome
            </Text>
            <Box bg="gray.100" borderRadius="md" p={{ base: 4, md: 3 }}>
              <Text
                fontSize={{ base: "xl", md: "md" }}
                color="gray.800"
                fontFamily="Lexend Deca"
              >
                {formData.personalData.name}
              </Text>
            </Box>
          </Box>

          <Box>
            <Text
              fontSize={{ base: "lg", md: "md" }}
              color="gray.600"
              mb={2}
              fontFamily="Lexend Deca"
            >
              CPF
            </Text>
            <Box bg="gray.100" borderRadius="md" p={{ base: 4, md: 3 }}>
              <Text
                fontSize={{ base: "xl", md: "md" }}
                color="gray.800"
                fontFamily="Lexend Deca"
              >
                {cpfFormatter(proposal?.NaturalPerson?.document!)}
              </Text>
            </Box>
          </Box>

          <Box>
            <Text
              fontSize={{ base: "lg", md: "md" }}
              color="gray.600"
              mb={2}
              fontFamily="Lexend Deca"
            >
              CEP
            </Text>
            <Box bg="gray.100" borderRadius="md" p={{ base: 4, md: 3 }}>
              <Text
                fontSize={{ base: "xl", md: "md" }}
                color="gray.800"
                fontFamily="Lexend Deca"
              >
                {cepFormatter(formData.address.cep)}
              </Text>
            </Box>
          </Box>

          <Box>
            <Text
              fontSize={{ base: "lg", md: "md" }}
              color="gray.600"
              mb={2}
              fontFamily="Lexend Deca"
            >
              Dados Bancários
            </Text>
            <Box bg="gray.100" borderRadius="md" p={{ base: 4, md: 3 }}>
              <Text
                fontSize={{ base: "xl", md: "md" }}
                color="gray.800"
                fontFamily="Lexend Deca"
              >
                {formData.bankData.pixType !== "BANK_ACCOUNT"
                  ? `Pix - ${getPixTypeLabel(formData.bankData.pixType)}: ${formData.bankData.pixKey}`
                  : `Conta ${formData.bankData.accountType} - Agência ${formData.bankData.agency}, Conta ${formData.bankData.account}-${formData.bankData.digit}`}
              </Text>
            </Box>
          </Box>

          <Box>
            <Text
              fontSize={{ base: "lg", md: "md" }}
              color="gray.600"
              mb={2}
              fontFamily="Lexend Deca"
            >
              Banco que vai bloquear o saldo
            </Text>
            <Box bg="gray.100" borderRadius="md" p={{ base: 4, md: 3 }}>
              <Text
                fontSize={{ base: "xl", md: "md" }}
                color="gray.800"
                fontFamily="Lexend Deca"
              >
                {formData.proposal!.chosenFinancialInfo?.Product?.processor?.name}
              </Text>
            </Box>
          </Box>
        </VStack>

        <Flex
          gap={{ base: 4, md: 5 }}
          justify="center"
          align="center"
          mt={{ base: 8, md: 6 }}
          flexDirection={{ base: "column", md: "row" }}
        >
          <Checkbox
            size="lg"
            colorScheme="green"
            isChecked={isChecked}
            onChange={handleCheckboxChange}
          >
            <Text
              fontSize={{ base: "xl", md: "lg" }}
              color="gray.700"
              textAlign="center"
            >
              Revisei as informações
            </Text>
          </Checkbox>

          <Button
            colorScheme="green"
            size={{ base: "lg", md: "lg" }}
            onClick={handleConfirmSubmit}
            isDisabled={!isChecked}
            px={{ base: 8, md: 6 }}
            py={{ base: 6, md: 3 }}
            fontSize={{ base: "xl", md: "lg" }}
            w={{ base: "100%", md: "auto" }}
            height={{ base: "60px", md: "auto" }}
          >
            Confirmar Proposta
          </Button>
        </Flex>
      </Box>

      <Modal isOpen={isLoading} onClose={() => {}} isCentered>
        <ModalOverlay />
        <ModalContent bg="transparent" boxShadow="none">
          <ModalBody display="flex" flexDirection="column" alignItems="center">
            <Logo variant="primary" w="100px" alt="Logo" />
            <Text mt={4} fontSize="xl" color="white" fontWeight="bold">
              Estamos enviando a sua proposta.
            </Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
